import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Collection, NavCollection, ProductSummary } from '@/types'

import Link from 'next/link'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { HomeHero } from '@/components/landing/home-hero'
import { CollectionTiles } from '@/components/landing/collection-tiles'
import { IconsCarousel } from '@/components/landing/icons-carousel'
import { SavoirFaireTeaser } from '@/components/landing/savoir-faire-teaser'
import { FragranceFeature } from '@/components/landing/fragrance-feature'
import { AppointmentTeaser } from '@/components/landing/appointment-teaser'
import { getCollections, getFeaturedProducts, getNavigation } from '@/lib/catalog'

interface HomeProps {
    navigation: Array<NavCollection>
    collections: Array<Collection>
    featured: Array<ProductSummary>
}

const Home: FC<HomeProps> = ({ navigation, collections, featured }): ReactNode => {
    return (
        <Layout navigation={navigation}>
            <Seo />

            <HomeHero />

            <section id='intro' aria-label='The Maison' className='px-6 py-24 sm:px-10 lg:py-40'>
                <div className='reveal mx-auto max-w-4xl text-center'>
                    <p className='eyebrow text-muted-foreground'>
                        Maison Vaurel, Paris, since 1948
                    </p>

                    <p className='mt-8 font-heading text-[1.85rem] font-light leading-[1.25] sm:text-4xl lg:text-[3.25rem] lg:leading-[1.15]'>
                        For more than seventy-five years, the Maison has dressed those who prefer the quiet confidence of things made well, made by hand and made to last.
                    </p>

                    <Link href='/maison' className='eyebrow link-underline mt-12 inline-block'>
                        Discover the Maison
                    </Link>
                </div>
            </section>

            <CollectionTiles collections={collections} />

            <IconsCarousel products={featured} />

            <SavoirFaireTeaser />

            <FragranceFeature />

            <AppointmentTeaser />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const navigation = await getNavigation()

    try {
        const [collections, featured] = await Promise.all([getCollections(), getFeaturedProducts(8)])

        return { props: { navigation, collections, featured }, revalidate: 60 }
    }

    catch (error) {
        console.error('Could not load the home page content:', error instanceof Error ? error.message : error)

        return { props: { navigation, collections: [], featured: [] }, revalidate: 30 }
    }
}

export default Home
